import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ApiError } from '@/lib/api-client'
import { decodeJwt, expiresAt } from '@/lib/jwt'
import { clearSession, getSession, setSession, subscribe, type AdminSession } from '@/lib/session'
import type { AuthResponse, LoginRequest } from '@/types/api'
import { login as loginRequest } from './api'
import {
  ADMIN_ROLE,
  AuthContext,
  NotAnAdminError,
  type AuthContextValue,
  type SignOutReason,
} from './auth-context'

/** setTimeout fires immediately past the 32-bit ceiling; a 30-day token needs clamping. */
const MAX_TIMEOUT_MS = 2_147_483_647

function toSession(response: AuthResponse): AdminSession {
  return {
    token: response.token,
    userId: response.userId,
    name: response.name,
    email: response.email,
    phone: response.phone,
    role: response.role,
    profileImageUrl: response.profileImageUrl,
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<AdminSession | null>(() => getSession())
  const [signOutReason, setSignOutReason] = useState<SignOutReason | null>(null)

  // The API client drops the session on a 401. Mirroring that here is what turns
  // a rejected request into a redirect to the login screen.
  useEffect(() => subscribe(setSessionState), [])

  useEffect(() => {
    if (!session) return

    const deadline = expiresAt(decodeJwt(session.token))
    if (deadline === null) return

    const remaining = deadline - Date.now()
    if (remaining <= 0) {
      clearSession()
      setSignOutReason('expired')
      return
    }

    // Sign out the moment the token dies rather than waiting for the next
    // request to fail, so a tab left open overnight does not greet its owner
    // with a screen full of errors.
    const timer = setTimeout(() => {
      clearSession()
      setSignOutReason('expired')
    }, Math.min(remaining, MAX_TIMEOUT_MS))

    return () => clearTimeout(timer)
  }, [session])

  const signIn = useCallback(async (credentials: LoginRequest) => {
    const response = await loginRequest(credentials)

    if (!response.token) {
      throw new ApiError('The server did not return a session token.', 500, 'NO_TOKEN')
    }

    // The API lets any account authenticate; only administrators may use this
    // console. Rejecting here avoids storing a token that would be refused by
    // every screen behind it.
    if ((response.role ?? '').toUpperCase() !== ADMIN_ROLE) {
      throw new NotAnAdminError('This account does not have administrator access to the operations console.')
    }
    if (response.enabled === false) {
      throw new NotAnAdminError('This account has been suspended. Contact another administrator to restore access.')
    }

    setSignOutReason(null)
    setSession(toSession(response))
  }, [])

  const signOut = useCallback((reason: SignOutReason = 'manual') => {
    clearSession()
    setSignOutReason(reason)
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: session !== null,
      signIn,
      signOut,
      signOutReason,
      clearSignOutReason: () => setSignOutReason(null),
    }),
    [session, signIn, signOut, signOutReason],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
